import axios from "@/libs/axios";
import { useChatsStore } from "@/store/useChatsStore";
import { ChatContact } from "@/views/modules/messaging-inbox/useChat";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import {
    Contact,
    CreateInternalComment,
    InternalComment,
    Label,
    Metadata,
    UpdateInternalComment,
} from "@saysimple-platform/dtos";
import { useQuery } from "@tanstack/vue-query";
import { clone } from "lodash";
import { defineStore } from "pinia";

interface State {
    isLoadingContact: boolean;
    isProcessingContact: boolean; // For example when editing internal comment.
    labels: Label[];
    allMetadata: Metadata[];
}

const endpoint = `${process.env.VUE_APP_PUBLIC_API}/contacts`;

/**
 * @NOTICE we do not have an "contacts" array state, because this is a getter, based on the results of the chatsStore fetch.
 * In that request, we also have the contacts data. We do NOT want a contacts fetch action, because we then fetch more than needed.
 *
 * This also means that when updating a contact, we update the contact by updating the chatsStore, so computed contacts is getriggered.
 */

export const useContactsStore = defineStore("contacts", {
    state: (): State => ({
        isLoadingContact: false,
        isProcessingContact: false,
        labels: [],
        allMetadata: [],
    }),
    actions: {
        async fetchContact(requestQueryBuilder?: RequestQueryBuilder) {
            const { data } = await axios.get(endpoint, {
                params: requestQueryBuilder?.queryObject,
            });

            return data[0];
        },
        fetchMetadataFields() {
            axios
                .get<Metadata[]>("/contacts/metadata", {
                    params: {
                        distinct: true,
                        fields: "name",
                    },
                })
                .then(({ data }) => (this.allMetadata = data));
        },
        // Method is kind of a "proxy" to chatsStore, but with the difference that its doing a backend request to update contact.
        updateContact(contact: Partial<Contact> & Pick<Contact, "id">) {
            this.isLoadingContact = true;

            if (contact.metadata) {
                // Filter out empty metadata to avoid empty values in the database.
                contact.metadata = contact.metadata.filter((field) => field.value || field.id);
            }

            // @todo do we still need this since email and phone are readonly fields?
            // return _checkDuplicates()

            return axios
                .patch(`${endpoint}/${contact.id}`, contact)
                .then((response) => {
                    // @NOTE that we are updating the CHATS store below if updating a contact.
                    const chatsStore = useChatsStore();

                    chatsStore.chatsList.forEach((chatsListChat) => {
                        if (chatsListChat.contact.id !== contact.id) {
                            return;
                        }

                        chatsStore.updateChat({
                            ...chatsListChat,
                            contact: {
                                ...chatsListChat.contact,
                                ...contact,
                            },
                        });
                    });

                    if (chatsStore.activeChat?.contact.id === contact.id) {
                        const updatedContact = {
                            ...chatsStore.activeChat?.contact,
                            ...contact,
                        };

                        // We may had no metadata ids, so set them if they're returned. Otherwise you cannot save a non-existing metadata link twice.
                        if (response.data.metadata) {
                            updatedContact.metadata = response.data.metadata;
                        }

                        chatsStore.activeChat.contact = updatedContact;
                    }

                    return response;
                })
                .finally(() => (this.isLoadingContact = false));
        },
        createInternalComment(internalComment: CreateInternalComment) {
            this.isProcessingContact = true;

            return axios
                .post(`${process.env.VUE_APP_PUBLIC_API}/internal-comments`, internalComment)
                .then(({ data }: { data: InternalComment }) => {
                    this.activeChatContact?.internalComments?.push(data);

                    return data;
                })
                .finally(() => (this.isProcessingContact = false));
        },
        updateInternalComment(internalComment: UpdateInternalComment) {
            this.isProcessingContact = true;

            return axios
                .patch(`${process.env.VUE_APP_PUBLIC_API}/internal-comments/${internalComment.id}`, internalComment)
                .then(({ data }: { data: InternalComment }) => {
                    if (!this.activeChatContact?.internalComments) {
                        return;
                    }

                    const currentInternalCommentIndex = this.activeChatContact.internalComments.findIndex(
                        (internalComment) => internalComment.id === data.id
                    );

                    if (typeof currentInternalCommentIndex === "undefined" || currentInternalCommentIndex === -1) {
                        return;
                    }

                    this.activeChatContact?.internalComments.splice(currentInternalCommentIndex, 1, {
                        ...this.activeChatContact?.internalComments[currentInternalCommentIndex],
                        ...{
                            comment: data.comment,
                        },
                    });

                    return data;
                })
                .finally(() => (this.isProcessingContact = false));
        },
        deleteInternalComment(internalCommentId: number) {
            this.isProcessingContact = true;

            return axios
                .delete(`${process.env.VUE_APP_PUBLIC_API}/internal-comments/${internalCommentId}`)
                .then(({ data }: { data: InternalComment }) => {
                    if (!this.activeChatContact?.internalComments) {
                        return;
                    }

                    const currentInternalCommentIndex = this.activeChatContact.internalComments.findIndex(
                        (internalComment) => internalComment.id === data.id
                    );

                    if (currentInternalCommentIndex === -1) {
                        return;
                    }

                    this.activeChatContact.internalComments.splice(currentInternalCommentIndex, 1);

                    return data;
                })
                .finally(() => (this.isProcessingContact = false));
        },
        fetchLabels(requestQueryBuilder: RequestQueryBuilder = new RequestQueryBuilder()) {
            useQuery(
                ["labels"],
                () => {
                    this.isLoadingContact = true;
                    return axios.get(`${process.env.VUE_APP_PUBLIC_API}/labels`, {
                        params: requestQueryBuilder?.queryObject,
                    });
                },
                {
                    select: (response: { data: Label[] }) => response.data,
                    staleTime: 60000,
                    placeholderData: { data: [] } as any,
                    onSuccess: (labels) => {
                        this.labels = labels;
                    },
                    onSettled: () => {
                        this.isLoadingContact = false;
                    },
                }
            );
        },
        createLabel(label: Label): Promise<Label> {
            this.isProcessingContact = true;

            return axios
                .post(`${process.env.VUE_APP_PUBLIC_API}/labels`, label)
                .then(({ data }) => {
                    this.labels.unshift(data);

                    return data;
                })
                .finally(() => (this.isProcessingContact = false));
        },
        updateLabel(labelId: number, partialLabel: Partial<Label>): Promise<Label> {
            this.isProcessingContact = true;

            const index = this.labels.findIndex((findLabel: Label) => findLabel.id === labelId);

            const label: Label = {
                ...this.labels[index],
                ...partialLabel,
            };

            return axios
                .patch(`${process.env.VUE_APP_PUBLIC_API}/labels/${labelId}`, label)
                .then(() => {
                    this.labels.splice(index, 1, label);

                    return label;
                })
                .finally(() => (this.isProcessingContact = false));
        },
        deleteLabel(labelId: number): Promise<number> {
            this.isProcessingContact = true;

            return axios
                .delete(`${process.env.VUE_APP_PUBLIC_API}/labels/${labelId}`)
                .then(({ data }) => {
                    const index = this.labels.findIndex((label: Label) => label.id === labelId);
                    this.labels.splice(index, 1);

                    return data.affected;
                })
                .finally(() => (this.isProcessingContact = false));
        },
        linkLabel(label: Label, contact: Pick<Contact, "id" | "labels">) {
            if (!contact) {
                return;
            }

            this.isLoadingContact = true;

            const labels = [...(contact?.labels ?? []), label];

            return axios
                .patch(`${endpoint}/${contact?.id}`, {
                    id: contact.id,
                    labels,
                })
                .then(({ data }) => {
                    const chatsStore = useChatsStore();

                    if (chatsStore.activeChat) {
                        chatsStore.activeChat.contact.labels = labels;
                    }

                    return data;
                })
                .finally(() => (this.isLoadingContact = false));
        },
        unlinkLabel(labelId: number, contact: Pick<Contact, "id" | "labels">) {
            if (!contact) {
                return;
            }

            const labelIndex = contact.labels.findIndex((label) => label.id === labelId);
            if (typeof labelIndex === "undefined" || labelIndex === -1) {
                return;
            }

            this.isLoadingContact = true;

            // Clone, so we can update the real labels when request succeeded, because splice will directly mutate the actual labels.
            const labels = clone(contact.labels) ?? [];
            labels.splice(labelIndex, 1);

            return axios
                .patch(`${endpoint}/${contact.id}`, {
                    id: contact.id,
                    labels,
                })
                .then(({ data }) => {
                    const chatsStore = useChatsStore();

                    if (chatsStore.activeChat) {
                        chatsStore.activeChat.contact.labels = labels;
                    }

                    return data;
                })
                .finally(() => (this.isLoadingContact = false));
        },
        getContactMetadata() {
            const chatsStore = useChatsStore();

            return this.allMetadata.map((meta) => {
                const contactMetadata = chatsStore.activeChat?.contact?.metadata.find(
                    (contactMetadata) => contactMetadata.name === meta.name
                );

                const nonContactMetadata = { ...meta, value: "" };
                chatsStore.activeChat?.contact?.metadata.push(nonContactMetadata);

                return contactMetadata ?? nonContactMetadata;
            });
        },
    },
    getters: {
        contacts() {
            const chatsStore = useChatsStore();
            return chatsStore.chatsList.map((chatContact) => chatContact.contact);
        },
        activeChatContact() {
            const chatsStore = useChatsStore();

            // Do not fetch from computed "contacts", because that will change when changing status filter.
            // This also means that when updating a contact, we should also update the activeChat contact.
            return { ...chatsStore.activeChat?.contact, metadata: this.getContactMetadata() };
        },
        getContactById(): (contactId: number) => ChatContact | undefined {
            return (contactId: number) => this.contacts?.filter(Boolean).find((contact) => contact.id === contactId);
        },
        hasInternalComments() {
            const chatsStore = useChatsStore();

            if (!chatsStore.activeChat?.contact.internalComments) {
                return false;
            }

            return chatsStore.activeChat?.contact.internalComments.length > 0;
        },
    },
});
